import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../auth/firebase';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import './technical_analytics.css';
import { 
  FaUserShield, 
  FaMobileAlt, 
  FaSignOutAlt, 
  FaUserMinus,
  FaExclamationTriangle,
  FaClock,
  FaBolt,
  FaCalendarAlt,
  FaUsers,
  FaSignInAlt,
  FaTrash,
  FaBan,
  FaExclamationCircle,
  FaTag,
  FaInfoCircle,
  FaUserSlash,
  FaUserAltSlash,
  FaTrashAlt,
  FaCalendarTimes
} from 'react-icons/fa';

const TechnicalAnalytics = () => {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  // # Tab state
  const [activeTab, setActiveTab] = useState('analytics');
  // # Debug state
  const [debug, setDebug] = useState('');

  // # Fetch available months
  useEffect(() => {
    const fetchMonths = async () => {
      try {
        // # Get all documents without ordering (we'll sort them in memory)
        const systemLogsRef = collection(db, 'system_logs');
        const querySnapshot = await getDocs(systemLogsRef);
        
        // # Extract IDs and sort them manually (newest first)
        const monthsList = querySnapshot.docs
          .map(doc => doc.id)
          .sort()
          .reverse(); // Sort in descending order (newest first)
        
        setMonths(monthsList);
        
        if (monthsList.length > 0) {
          setSelectedMonth(monthsList[0]);
          setDebug(`Found ${monthsList.length} months: ${monthsList.join(', ')}`);
        } else {
          setError('No available data');
          setLoading(false);
          setDebug('No months found in system_logs collection');
        }
      } catch (err) {
        console.error('Error fetching months:', err);
        setError('No available data');
        setLoading(false);
        setDebug(`Error fetching months: ${err.message}`);
      }
    };
    fetchMonths();
  }, []);

  // # Fetch data for selected month
  useEffect(() => {
    const fetchMonthData = async () => {
      if (!selectedMonth) return;
      
      setLoading(true);
      try {
        const docRef = doc(db, 'system_logs', selectedMonth);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("Retrieved data:", data); // Debug log
          
          // # Check if any data exists in the document
          if (data && Object.keys(data).length > 0) {
            setAnalyticsData(data);
            setError(null);
            
            // # Set debug info
            setDebug(`Found data for ${selectedMonth}: ${Object.keys(data).join(', ')}`);
          } else {
            setAnalyticsData(null);
            setError('No available data');
            setDebug(`Document exists but has no data fields`);
          }
        } else {
          setAnalyticsData(null);
          setError('No available data');
          setDebug(`No document found for ${selectedMonth}`);
        }
      } catch (err) {
        console.error('Error fetching month data:', err);
        setError('No available data');
        setAnalyticsData(null);
        setDebug(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchMonthData();
  }, [selectedMonth]);

  // # Update the renderAnalyticsSection function to show actual analytics
  const renderAnalyticsSection = () => {
    if (!analyticsData) return null;
    
    // # Calculate summary statistics from the data
    const stats = {
      authCleanup: {
        total: 0,
        averageProcessedUsers: 0,
        averageExecutionTime: 0,
        totalDeletedItems: 0,
        successRate: 100
      },
      deviceCleanup: {
        total: 0,
        processedDevices: 0,
        removedDevices: 0,
        averageExecutionTime: 0,
        successRate: 100
      },
      signOuts: {
        total: 0,
        processedDevices: 0,
        signedOutDevices: 0,
        revokedSessions: 0,
        averageExecutionTime: 0
      },
      subscriptionChecks: {
        total: 0,
        totalProcessed: 0,
        totalUpdated: 0,
        averageExecutionTime: 0,
        successRate: 100
      },
      errors: {
        total: 0,
        byType: {
          auth: 0,
          device: 0,
          signOut: 0,
          subscription: 0,
          unverified: 0,
          incomplete: 0,
          oldMeetings: 0,
          other: 0
        }
      },
      unverifiedCleanup: {
        total: 0,
        processedUsers: 0,
        unverifiedUsersFound: 0,
        deletedUsers: 0,
        averageExecutionTime: 0,
        successRate: 100
      },
      incompleteCleanup: {
        total: 0,
        processedUsers: 0,
        incompleteUsersFound: 0,
        deletedUsers: 0,
        averageExecutionTime: 0,
        successRate: 100
      },
      oldMeetingsCleanup: {
        total: 0,
        totalMeetingsProcessed: 0,
        oldMeetingsFound: 0,
        meetingsDeleted: 0,
        notificationsRemoved: 0,
        averageExecutionTime: 0,
        successRate: 100
      }
    };
    
    // # Process auth cleanup data
    if (analyticsData.auth_cleanup) {
      const cleanups = Object.values(analyticsData.auth_cleanup);
      stats.authCleanup.total = cleanups.length;
      
      let totalProcessedUsers = 0;
      let totalExecutionTime = 0;
      let totalDeletedSignIns = 0;
      let totalDeletedSignOuts = 0;
      
      cleanups.forEach(cleanup => {
        if (cleanup.stats) {
          totalProcessedUsers += cleanup.stats.processedUsers || 0;
          totalDeletedSignIns += cleanup.stats.deletedSignIns || 0;
          totalDeletedSignOuts += cleanup.stats.deletedSignOuts || 0;
        }
        totalExecutionTime += cleanup.executionTime || 0;
      });
      
      stats.authCleanup.averageProcessedUsers = totalProcessedUsers / stats.authCleanup.total || 0;
      stats.authCleanup.averageExecutionTime = totalExecutionTime / stats.authCleanup.total || 0;
      stats.authCleanup.totalDeletedItems = totalDeletedSignIns + totalDeletedSignOuts;
    }
    
    // # Process device cleanup data
    if (analyticsData.device_cleanup) {
      const cleanups = Object.values(analyticsData.device_cleanup);
      stats.deviceCleanup.total = cleanups.length;
      
      let totalProcessedDevices = 0;
      let totalRemovedDevices = 0;
      let totalExecutionTime = 0;
      
      cleanups.forEach(cleanup => {
        if (cleanup.stats) {
          totalProcessedDevices += cleanup.stats.processedDevices || 0;
          totalRemovedDevices += cleanup.stats.removedDevices || 0;
        }
        totalExecutionTime += cleanup.executionTime || 0;
      });
      
      stats.deviceCleanup.processedDevices = totalProcessedDevices;
      stats.deviceCleanup.removedDevices = totalRemovedDevices;
      stats.deviceCleanup.averageExecutionTime = totalExecutionTime / stats.deviceCleanup.total || 0;
    }
    
    // # Process sign-out data (both mass and specific)
    const processSignOuts = (data, type) => {
      if (!data) return;
      
      const signOuts = Object.values(data);
      stats.signOuts.total += signOuts.length;
      
      let totalProcessedDevices = 0;
      let totalSignedOutDevices = 0;
      let totalRevokedSessions = 0;
      let totalExecutionTime = 0;
      
      signOuts.forEach(signOut => {
        if (signOut.stats) {
          totalProcessedDevices += signOut.stats.processedDevices || 0;
          totalSignedOutDevices += signOut.stats.signedOutDevices || 0;
          totalRevokedSessions += signOut.stats.revokedSessions || 0;
        }
        totalExecutionTime += signOut.executionTime || 0;
      });
      
      stats.signOuts.processedDevices += totalProcessedDevices;
      stats.signOuts.signedOutDevices += totalSignedOutDevices;
      stats.signOuts.revokedSessions += totalRevokedSessions;
      stats.signOuts.averageExecutionTime = (stats.signOuts.averageExecutionTime * (stats.signOuts.total - signOuts.length) + totalExecutionTime) / stats.signOuts.total || 0;
    };
    
    processSignOuts(analyticsData.sign_all_outs, 'mass');
    processSignOuts(analyticsData.sign_specific_users, 'specific');
    
    // # Process subscription check data (both scheduled and manual)
    const processSubscriptionChecks = (data, type) => {
      if (!data) return;
      
      const checks = Object.values(data);
      stats.subscriptionChecks.total += checks.length;
      
      let totalProcessed = 0;
      let totalUpdated = 0;
      let totalExecutionTime = 0;
      
      checks.forEach(check => {
        if (check.stats) {
          totalProcessed += check.stats.totalProcessed || 0;
          totalUpdated += check.stats.updated || 0;
        }
        totalExecutionTime += check.executionTime || 0;
      });
      
      stats.subscriptionChecks.totalProcessed += totalProcessed;
      stats.subscriptionChecks.totalUpdated += totalUpdated;
      
      if (stats.subscriptionChecks.total > 0) {
        stats.subscriptionChecks.averageExecutionTime = 
          (stats.subscriptionChecks.averageExecutionTime * (stats.subscriptionChecks.total - checks.length) + 
           totalExecutionTime) / stats.subscriptionChecks.total || 0;
      }
    };
    
    processSubscriptionChecks(analyticsData.check_subscriptions, 'scheduled');
    processSubscriptionChecks(analyticsData.manual_check_subscriptions, 'manual');
    
    // # Process unverified cleanup data
    if (analyticsData.unverified_cleanup) {
      const cleanups = Object.values(analyticsData.unverified_cleanup);
      stats.unverifiedCleanup.total = cleanups.length;
      
      let totalProcessedUsers = 0;
      let totalUnverifiedFound = 0;
      let totalDeletedUsers = 0;
      let totalExecutionTime = 0;
      
      cleanups.forEach(cleanup => {
        if (cleanup.stats) {
          totalProcessedUsers += cleanup.stats.processedUsers || 0;
          totalUnverifiedFound += cleanup.stats.unverifiedUsersFound || 0;
          totalDeletedUsers += cleanup.stats.deletedUsers || 0;
        }
        totalExecutionTime += cleanup.executionTime || 0;
      });
      
      stats.unverifiedCleanup.processedUsers = totalProcessedUsers;
      stats.unverifiedCleanup.unverifiedUsersFound = totalUnverifiedFound;
      stats.unverifiedCleanup.deletedUsers = totalDeletedUsers;
      stats.unverifiedCleanup.averageExecutionTime = totalExecutionTime / stats.unverifiedCleanup.total || 0;
    }
    
    // # Add incomplete signup cleanup stats
    stats.incompleteCleanup = {
      total: 0,
      processedUsers: 0,
      incompleteUsersFound: 0,
      deletedUsers: 0,
      averageExecutionTime: 0,
      successRate: 100
    };
    
    // # Process incomplete signup cleanup data
    if (analyticsData.incomplete_signup_cleanups) {
      const cleanups = Object.values(analyticsData.incomplete_signup_cleanups);
      stats.incompleteCleanup.total = cleanups.length;
      
      let totalProcessedUsers = 0;
      let totalIncompleteFound = 0;
      let totalDeletedUsers = 0;
      let totalExecutionTime = 0;
      
      cleanups.forEach(cleanup => {
        if (cleanup.stats) {
          totalProcessedUsers += cleanup.stats.processedUsers || 0;
          totalIncompleteFound += cleanup.stats.incompleteUsersFound || 0;
          totalDeletedUsers += cleanup.stats.deletedUsers || 0;
        }
        totalExecutionTime += cleanup.executionTime || 0;
      });
      
      stats.incompleteCleanup.processedUsers = totalProcessedUsers;
      stats.incompleteCleanup.incompleteUsersFound = totalIncompleteFound;
      stats.incompleteCleanup.deletedUsers = totalDeletedUsers;
      stats.incompleteCleanup.averageExecutionTime = totalExecutionTime / stats.incompleteCleanup.total || 0;
    }
    
    // # Process old meetings cleanup data
    if (analyticsData.old_meetings_cleanups) {
      const cleanups = Object.values(analyticsData.old_meetings_cleanups);
      stats.oldMeetingsCleanup.total = cleanups.length;
      
      let totalMeetingsProcessed = 0;
      let totalOldMeetingsFound = 0;
      let totalMeetingsDeleted = 0;
      let totalNotificationsRemoved = 0;
      let totalExecutionTime = 0;
      
      cleanups.forEach(cleanup => {
        if (cleanup.stats) {
          totalMeetingsProcessed += cleanup.stats.totalMeetingsProcessed || 0;
          totalOldMeetingsFound += cleanup.stats.oldMeetingsFound || 0;
          totalMeetingsDeleted += cleanup.stats.meetingsDeleted || 0;
          totalNotificationsRemoved += cleanup.stats.notificationsRemoved || 0;
        }
        totalExecutionTime += cleanup.executionTime || 0;
      });
      
      stats.oldMeetingsCleanup.totalMeetingsProcessed = totalMeetingsProcessed;
      stats.oldMeetingsCleanup.oldMeetingsFound = totalOldMeetingsFound;
      stats.oldMeetingsCleanup.meetingsDeleted = totalMeetingsDeleted;
      stats.oldMeetingsCleanup.notificationsRemoved = totalNotificationsRemoved;
      stats.oldMeetingsCleanup.averageExecutionTime = totalExecutionTime / stats.oldMeetingsCleanup.total || 0;
    }
    
    // # Process error data
    const countErrors = (data, type) => {
      if (!data) return 0;
      const count = Object.keys(data).length;
      stats.errors.total += count;
      stats.errors.byType[type] = (stats.errors.byType[type] || 0) + count;
      return count;
    };
    
    const authErrors = countErrors(analyticsData.auth_cleanup_errors, 'auth');
    const deviceErrors = countErrors(analyticsData.device_cleanup_errors, 'device');
    const massSignOutErrors = countErrors(analyticsData.sign_all_out_errors, 'signOut');
    const specificSignOutErrors = countErrors(analyticsData.sign_specific_user_errors, 'signOut');
    const scheduledSubCheckErrors = countErrors(analyticsData.check_subscriptions_errors, 'subscription');
    const manualSubCheckErrors = countErrors(analyticsData.manual_check_subscriptions_errors, 'subscription');
    const unverifiedCleanupErrors = countErrors(analyticsData.unverified_cleanup_errors, 'unverified');
    const incompleteCleanupErrors = countErrors(analyticsData.incomplete_signup_cleanup_errors, 'incomplete');
    const oldMeetingsCleanupErrors = countErrors(analyticsData.old_meetings_cleanup_errors, 'oldMeetings');
    
    // # Calculate success rates with null checks
    if (stats.authCleanup.total > 0) {
      stats.authCleanup.successRate = 100 - (authErrors / (stats.authCleanup.total + authErrors) * 100) || 0;
    }
    
    if (stats.deviceCleanup.total > 0) {
      stats.deviceCleanup.successRate = 100 - (deviceErrors / (stats.deviceCleanup.total + deviceErrors) * 100) || 0;
    }
    
    if (stats.signOuts.total > 0) {
      stats.signOuts.successRate = 100 - ((massSignOutErrors + specificSignOutErrors) / 
        (stats.signOuts.total + massSignOutErrors + specificSignOutErrors) * 100) || 0;
    }
    
    if (stats.subscriptionChecks.total > 0) {
      stats.subscriptionChecks.successRate = 100 - ((scheduledSubCheckErrors + manualSubCheckErrors) / 
        (stats.subscriptionChecks.total + scheduledSubCheckErrors + manualSubCheckErrors) * 100) || 0;
    }
    
    if (stats.unverifiedCleanup.total > 0) {
      stats.unverifiedCleanup.successRate = 100 - (unverifiedCleanupErrors / (stats.unverifiedCleanup.total + unverifiedCleanupErrors) * 100) || 0;
    }
    
    if (stats.incompleteCleanup.total > 0) {
      stats.incompleteCleanup.successRate = 100 - (incompleteCleanupErrors / (stats.incompleteCleanup.total + incompleteCleanupErrors) * 100) || 0;
    }
    
    if (stats.oldMeetingsCleanup.total > 0) {
      stats.oldMeetingsCleanup.successRate = 100 - (oldMeetingsCleanupErrors / (stats.oldMeetingsCleanup.total + oldMeetingsCleanupErrors) * 100) || 0;
    }
    
    // # Format time functions
    const formatTime = (ms) => {
      if (!ms || isNaN(ms)) return '0ms';
      if (ms < 1000) return `${ms.toFixed(0)}ms`;
      return `${(ms / 1000).toFixed(2)}s`;
    };
    
    const formatPercent = (value) => {
      if (!value || isNaN(value)) return '0.0%';
      return `${value.toFixed(1)}%`;
    };
    
    // # Render the analytics dashboard
    return (
      <div className="tech_analytics_dashboard">
        {/* Overall summary card */}
        <div className="tech_summary_card">
          <h3>Monthly System Operations Summary</h3>
          <div className="tech_summary_stats">
            <div className="tech_summary_stat">
              <FaUserShield className="tech_stat_icon" />
              <div className="tech_stat_content">
                <span className="tech_stat_value">{stats.authCleanup.total}</span>
                <span className="tech_stat_label">Auth Cleanups</span>
              </div>
            </div>
            <div className="tech_summary_stat">
              <FaMobileAlt className="tech_stat_icon" />
              <div className="tech_stat_content">
                <span className="tech_stat_value">{stats.deviceCleanup.total}</span>
                <span className="tech_stat_label">Device Cleanups</span>
              </div>
            </div>
            <div className="tech_summary_stat">
              <FaSignOutAlt className="tech_stat_icon" />
              <div className="tech_stat_content">
                <span className="tech_stat_value">{stats.signOuts.total}</span>
                <span className="tech_stat_label">Sign-out Operations</span>
              </div>
            </div>
            <div className="tech_summary_stat">
              <FaCalendarAlt className="tech_stat_icon" />
              <div className="tech_stat_content">
                <span className="tech_stat_value">{stats.subscriptionChecks.total}</span>
                <span className="tech_stat_label">Subscription Checks</span>
              </div>
            </div>
            <div className="tech_summary_stat">
              <FaExclamationTriangle className="tech_stat_icon tech_error_icon" />
              <div className="tech_stat_content">
                <span className="tech_stat_value">{stats.errors.total}</span>
                <span className="tech_stat_label">Total Errors</span>
              </div>
            </div>
            <div className="tech_summary_stat">
              <FaUserSlash className="tech_stat_icon" />
              <div className="tech_stat_content">
                <span className="tech_stat_value">{stats.unverifiedCleanup.total}</span>
                <span className="tech_stat_label">Unverified Cleanups</span>
              </div>
            </div>
            <div className="tech_summary_stat">
              <FaUserAltSlash className="tech_stat_icon" />
              <div className="tech_stat_content">
                <span className="tech_stat_value">{stats.incompleteCleanup.total}</span>
                <span className="tech_stat_label">Incomplete Cleanups</span>
              </div>
            </div>
            <div className="tech_summary_stat">
              <FaCalendarTimes className="tech_stat_icon" />
              <div className="tech_stat_content">
                <span className="tech_stat_value">{stats.oldMeetingsCleanup.total}</span>
                <span className="tech_stat_label">Old Meetings Cleanups</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Success Rates */}
        <div className="tech_analytics_section">
          <h3>Operation Success Rates</h3>
          <div className="tech_success_rates">
            <div className="tech_success_rate_item">
              <div className="tech_rate_header">
                <FaUserShield />
                <span>Auth Cleanup</span>
              </div>
              <div className="tech_progress_bar">
                <div 
                  className="tech_progress_fill" 
                  style={{ width: `${stats.authCleanup.successRate}%` }}
                ></div>
              </div>
              <div className="tech_rate_value">{formatPercent(stats.authCleanup.successRate)}</div>
            </div>
            
            <div className="tech_success_rate_item">
              <div className="tech_rate_header">
                <FaMobileAlt />
                <span>Device Cleanup</span>
              </div>
              <div className="tech_progress_bar">
                <div 
                  className="tech_progress_fill" 
                  style={{ width: `${stats.deviceCleanup.successRate}%` }}
                ></div>
              </div>
              <div className="tech_rate_value">{formatPercent(stats.deviceCleanup.successRate)}</div>
            </div>
            
            <div className="tech_success_rate_item">
              <div className="tech_rate_header">
                <FaSignOutAlt />
                <span>Sign-out Operations</span>
              </div>
              <div className="tech_progress_bar">
                <div 
                  className="tech_progress_fill" 
                  style={{ width: `${stats.signOuts.successRate}%` }}
                ></div>
              </div>
              <div className="tech_rate_value">{formatPercent(stats.signOuts.successRate)}</div>
            </div>
            
            <div className="tech_success_rate_item">
              <div className="tech_rate_header">
                <FaCalendarAlt />
                <span>Subscription Checks</span>
              </div>
              <div className="tech_progress_bar">
                <div 
                  className="tech_progress_fill" 
                  style={{ width: `${stats.subscriptionChecks.successRate}%` }}
                ></div>
              </div>
              <div className="tech_rate_value">{formatPercent(stats.subscriptionChecks.successRate)}</div>
            </div>
            
            <div className="tech_success_rate_item">
              <div className="tech_rate_header">
                <FaUserSlash />
                <span>Unverified Cleanup</span>
              </div>
              <div className="tech_progress_bar">
                <div 
                  className="tech_progress_fill" 
                  style={{ width: `${stats.unverifiedCleanup.successRate}%` }}
                ></div>
              </div>
              <div className="tech_rate_value">{formatPercent(stats.unverifiedCleanup.successRate)}</div>
            </div>
            
            <div className="tech_success_rate_item">
              <div className="tech_rate_header">
                <FaUserAltSlash />
                <span>Incomplete Signup Cleanup</span>
              </div>
              <div className="tech_progress_bar">
                <div 
                  className="tech_progress_fill" 
                  style={{ width: `${stats.incompleteCleanup.successRate}%` }}
                ></div>
              </div>
              <div className="tech_rate_value">{formatPercent(stats.incompleteCleanup.successRate)}</div>
            </div>
            
            <div className="tech_success_rate_item">
              <div className="tech_rate_header">
                <FaCalendarTimes />
                <span>Old Meetings Cleanup</span>
              </div>
              <div className="tech_progress_bar">
                <div 
                  className="tech_progress_fill" 
                  style={{ width: `${stats.oldMeetingsCleanup.successRate}%` }}
                ></div>
              </div>
              <div className="tech_rate_value">{formatPercent(stats.oldMeetingsCleanup.successRate)}</div>
            </div>
          </div>
        </div>
        
        {/* Detailed Analytic Sections */}
        <div className="tech_analytics_grid">
          {/* Auth Cleanup Analytics */}
          {stats.authCleanup.total > 0 && (
            <div className="tech_analytics_card">
              <div className="tech_card_header">
                <div className="tech_card_title">
                  <FaUserShield />
                  <h4>Authentication Cleanup</h4>
                </div>
              </div>
              <div className="tech_stats_list">
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Avg. Users Processed</span>
                  <span className="tech_stat_value">{stats.authCleanup.averageProcessedUsers.toFixed(0)}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Total Items Deleted</span>
                  <span className="tech_stat_value">{stats.authCleanup.totalDeletedItems}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Avg. Execution Time</span>
                  <span className="tech_stat_value">{formatTime(stats.authCleanup.averageExecutionTime)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Device Cleanup Analytics */}
          {stats.deviceCleanup.total > 0 && (
            <div className="tech_analytics_card">
              <div className="tech_card_header">
                <div className="tech_card_title">
                  <FaMobileAlt />
                  <h4>Device Cleanup</h4>
                </div>
              </div>
              <div className="tech_stats_list">
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Devices Processed</span>
                  <span className="tech_stat_value">{stats.deviceCleanup.processedDevices}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Devices Removed</span>
                  <span className="tech_stat_value">{stats.deviceCleanup.removedDevices}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Cleanup Efficiency</span>
                  <span className="tech_stat_value">
                    {stats.deviceCleanup.processedDevices > 0 
                      ? formatPercent((stats.deviceCleanup.removedDevices / stats.deviceCleanup.processedDevices) * 100)
                      : 'N/A'}
                  </span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Avg. Execution Time</span>
                  <span className="tech_stat_value">{formatTime(stats.deviceCleanup.averageExecutionTime)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Sign-out Analytics */}
          {stats.signOuts.total > 0 && (
            <div className="tech_analytics_card">
              <div className="tech_card_header">
                <div className="tech_card_title">
                  <FaSignOutAlt />
                  <h4>Sign-out Operations</h4>
                </div>
              </div>
              <div className="tech_stats_list">
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Devices Processed</span>
                  <span className="tech_stat_value">{stats.signOuts.processedDevices}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Devices Signed Out</span>
                  <span className="tech_stat_value">{stats.signOuts.signedOutDevices}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Sessions Revoked</span>
                  <span className="tech_stat_value">{stats.signOuts.revokedSessions}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Avg. Execution Time</span>
                  <span className="tech_stat_value">{formatTime(stats.signOuts.averageExecutionTime)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Subscription Check Analytics */}
          {stats.subscriptionChecks.total > 0 && (
            <div className="tech_analytics_card">
              <div className="tech_card_header">
                <div className="tech_card_title">
                  <FaCalendarAlt />
                  <h4>Subscription Checks</h4>
                </div>
              </div>
              <div className="tech_stats_list">
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Subscriptions Processed</span>
                  <span className="tech_stat_value">{stats.subscriptionChecks.totalProcessed}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Subscriptions Updated</span>
                  <span className="tech_stat_value">{stats.subscriptionChecks.totalUpdated}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Update Rate</span>
                  <span className="tech_stat_value">
                    {stats.subscriptionChecks.totalProcessed > 0 
                      ? formatPercent((stats.subscriptionChecks.totalUpdated / stats.subscriptionChecks.totalProcessed) * 100)
                      : 'N/A'}
                  </span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Avg. Execution Time</span>
                  <span className="tech_stat_value">{formatTime(stats.subscriptionChecks.averageExecutionTime)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Unverified Cleanup Analytics */}
          <div className="tech_analytics_card">
            <div className="tech_card_header">
              <div className="tech_card_title">
                <FaUserSlash />
                <h4>Unverified User Cleanup</h4>
              </div>
            </div>
            <div className="tech_stats_list">
              <div className="tech_stat_row">
                <span className="tech_stat_name">Users Processed</span>
                <span className="tech_stat_value">{stats.unverifiedCleanup.processedUsers}</span>
              </div>
              <div className="tech_stat_row">
                <span className="tech_stat_name">Unverified Found</span>
                <span className="tech_stat_value">{stats.unverifiedCleanup.unverifiedUsersFound}</span>
              </div>
              <div className="tech_stat_row">
                <span className="tech_stat_name">Users Deleted</span>
                <span className="tech_stat_value">{stats.unverifiedCleanup.deletedUsers}</span>
              </div>
              <div className="tech_stat_row">
                <span className="tech_stat_name">Cleanup Rate</span>
                <span className="tech_stat_value">
                  {stats.unverifiedCleanup.unverifiedUsersFound > 0 
                    ? formatPercent((stats.unverifiedCleanup.deletedUsers / stats.unverifiedCleanup.unverifiedUsersFound) * 100)
                    : 'N/A'}
                </span>
              </div>
              <div className="tech_stat_row">
                <span className="tech_stat_name">Avg. Execution Time</span>
                <span className="tech_stat_value">{formatTime(stats.unverifiedCleanup.averageExecutionTime)}</span>
              </div>
            </div>
          </div>
          
          {/* Incomplete Cleanup Analytics */}
          {stats.incompleteCleanup.total > 0 && (
            <div className="tech_analytics_card">
              <div className="tech_card_header">
                <div className="tech_card_title">
                  <FaUserAltSlash />
                  <h4>Incomplete Signup Cleanup</h4>
                </div>
              </div>
              <div className="tech_stats_list">
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Users Processed</span>
                  <span className="tech_stat_value">{stats.incompleteCleanup.processedUsers}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Incomplete Found</span>
                  <span className="tech_stat_value">{stats.incompleteCleanup.incompleteUsersFound}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Users Deleted</span>
                  <span className="tech_stat_value">{stats.incompleteCleanup.deletedUsers}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Cleanup Rate</span>
                  <span className="tech_stat_value">
                    {stats.incompleteCleanup.incompleteUsersFound > 0 
                      ? formatPercent((stats.incompleteCleanup.deletedUsers / stats.incompleteCleanup.incompleteUsersFound) * 100)
                      : 'N/A'}
                  </span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Avg. Execution Time</span>
                  <span className="tech_stat_value">{formatTime(stats.incompleteCleanup.averageExecutionTime)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Old Meetings Cleanup Analytics */}
          {stats.oldMeetingsCleanup.total > 0 && (
            <div className="tech_analytics_card">
              <div className="tech_card_header">
                <div className="tech_card_title">
                  <FaCalendarTimes />
                  <h4>Old Meetings Cleanup</h4>
                </div>
              </div>
              <div className="tech_stats_list">
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Meetings Processed</span>
                  <span className="tech_stat_value">{stats.oldMeetingsCleanup.totalMeetingsProcessed}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Old Meetings Found</span>
                  <span className="tech_stat_value">{stats.oldMeetingsCleanup.oldMeetingsFound}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Meetings Deleted</span>
                  <span className="tech_stat_value">{stats.oldMeetingsCleanup.meetingsDeleted}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Notifications Removed</span>
                  <span className="tech_stat_value">{stats.oldMeetingsCleanup.notificationsRemoved}</span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Cleanup Rate</span>
                  <span className="tech_stat_value">
                    {stats.oldMeetingsCleanup.oldMeetingsFound > 0 
                      ? formatPercent((stats.oldMeetingsCleanup.meetingsDeleted / stats.oldMeetingsCleanup.oldMeetingsFound) * 100)
                      : 'N/A'}
                  </span>
                </div>
                <div className="tech_stat_row">
                  <span className="tech_stat_name">Avg. Execution Time</span>
                  <span className="tech_stat_value">{formatTime(stats.oldMeetingsCleanup.averageExecutionTime)}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Error Distribution */}
          {stats.errors.total > 0 && (
            <div className="tech_analytics_card">
              <div className="tech_card_header">
                <div className="tech_card_title">
                  <FaExclamationTriangle className="tech_error_icon" />
                  <h4>Error Distribution</h4>
                </div>
              </div>
              <div className="tech_error_distribution">
                {stats.errors.byType.auth > 0 && (
                  <div className="tech_error_segment">
                    <div className="tech_error_bar" style={{ 
                      width: `${(stats.errors.byType.auth / stats.errors.total) * 100}%`,
                      backgroundColor: '#4a6cf7'
                    }}></div>
                    <div className="tech_error_label">
                      <FaUserShield />
                      <span>Auth: {stats.errors.byType.auth}</span>
                    </div>
                  </div>
                )}
                
                {stats.errors.byType.device > 0 && (
                  <div className="tech_error_segment">
                    <div className="tech_error_bar" style={{ 
                      width: `${(stats.errors.byType.device / stats.errors.total) * 100}%`,
                      backgroundColor: '#10b981'
                    }}></div>
                    <div className="tech_error_label">
                      <FaMobileAlt />
                      <span>Device: {stats.errors.byType.device}</span>
                    </div>
                  </div>
                )}
                
                {stats.errors.byType.signOut > 0 && (
                  <div className="tech_error_segment">
                    <div className="tech_error_bar" style={{ 
                      width: `${(stats.errors.byType.signOut / stats.errors.total) * 100}%`,
                      backgroundColor: '#8b5cf6'
                    }}></div>
                    <div className="tech_error_label">
                      <FaSignOutAlt />
                      <span>Sign-out: {stats.errors.byType.signOut}</span>
                    </div>
                  </div>
                )}
                
                {stats.errors.byType.subscription > 0 && (
                  <div className="tech_error_segment">
                    <div className="tech_error_bar" style={{ 
                      width: `${(stats.errors.byType.subscription / stats.errors.total) * 100}%`,
                      backgroundColor: '#0ea5e9'
                    }}></div>
                    <div className="tech_error_label">
                      <FaCalendarAlt />
                      <span>Subscription: {stats.errors.byType.subscription}</span>
                    </div>
                  </div>
                )}
                
                {stats.errors.byType.unverified > 0 && (
                  <div className="tech_error_segment">
                    <div className="tech_error_bar" style={{ 
                      width: `${(stats.errors.byType.unverified / stats.errors.total) * 100}%`,
                      backgroundColor: '#e84393'
                    }}></div>
                    <div className="tech_error_label">
                      <FaUserSlash />
                      <span>Unverified: {stats.errors.byType.unverified}</span>
                    </div>
                  </div>
                )}
                
                {stats.errors.byType.incomplete > 0 && (
                  <div className="tech_error_segment">
                    <div className="tech_error_bar" style={{ 
                      width: `${(stats.errors.byType.incomplete / stats.errors.total) * 100}%`,
                      backgroundColor: '#ef4444'
                    }}></div>
                    <div className="tech_error_label">
                      <FaUserAltSlash />
                      <span>Incomplete: {stats.errors.byType.incomplete}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // # Update renderRawLogs function to include all possible log types
  const renderRawLogs = () => {
    if (!analyticsData) return null;

    // # Define log types with icons and colors
    const logTypes = {
      // Regular operations
      auth_cleanup: { icon: <FaUserShield />, label: 'Auth Cleanup', color: '#4a6cf7' },
      device_cleanup: { icon: <FaMobileAlt />, label: 'Device Cleanup', color: '#10b981' },
      sign_all_outs: { icon: <FaSignOutAlt />, label: 'Mass Sign-out', color: '#6d28d9' },
      sign_specific_users: { icon: <FaUserMinus />, label: 'Individual Sign-out', color: '#8b5cf6' },
      check_subscriptions: { icon: <FaCalendarAlt />, label: 'Subscription Check', color: '#0ea5e9' },
      manual_check_subscriptions: { icon: <FaCalendarAlt />, label: 'Manual Subscription Check', color: '#0ea5e9' },
      unverified_cleanup: { icon: <FaUserSlash />, label: 'Unverified Cleanup', color: '#e84393' },
      incomplete_signup_cleanup: { icon: <FaUserAltSlash />, label: 'Incomplete Signup Cleanup', color: '#ef4444' },
      old_meetings_cleanup: { icon: <FaCalendarTimes />, label: 'Old Meetings Cleanup', color: '#f59e0b' },
      unverified_cleanups: { icon: <FaUserSlash />, label: 'Unverified Cleanup', color: '#e84393' },
      incomplete_signup_cleanups: { icon: <FaUserAltSlash />, label: 'Incomplete Signup Cleanup', color: '#ef4444' },
      old_meetings_cleanups: { icon: <FaCalendarTimes />, label: 'Old Meetings Cleanup', color: '#f59e0b' },
      
      // Additional operation types - match actual type values from data
      scheduled_check: { icon: <FaCalendarAlt />, label: 'Scheduled Check', color: '#0ea5e9' },
      manual_check: { icon: <FaCalendarAlt />, label: 'Manual Check', color: '#0ea5e9' },
      signout_all: { icon: <FaSignOutAlt />, label: 'Mass Sign-out', color: '#6d28d9' },
      admin_user_deletion: { icon: <FaTrash />, label: 'Admin User Deletion', color: '#dc2626' },
      user_self_deletion: { icon: <FaBan />, label: 'User Self Deletion', color: '#dc2626' },
      user_self_deletion_error: { icon: <FaExclamationTriangle />, label: 'User Self Deletion Error', color: '#ef4444' },
      
      // Error types
      auth_cleanup_errors: { icon: <FaExclamationTriangle />, label: 'Auth Cleanup Error', color: '#ef4444' },
      device_cleanup_errors: { icon: <FaExclamationTriangle />, label: 'Device Cleanup Error', color: '#ef4444' },
      sign_all_out_errors: { icon: <FaExclamationTriangle />, label: 'Mass Sign-out Error', color: '#ef4444' },
      sign_specific_user_errors: { icon: <FaExclamationTriangle />, label: 'Individual Sign-out Error', color: '#ef4444' },
      check_subscriptions_errors: { icon: <FaExclamationTriangle />, label: 'Subscription Check Error', color: '#ef4444' },
      manual_check_subscriptions_errors: { icon: <FaExclamationTriangle />, label: 'Manual Subscription Check Error', color: '#ef4444' },
      unverified_cleanup_errors: { icon: <FaExclamationTriangle />, label: 'Unverified Cleanup Error', color: '#ef4444' },
      incomplete_signup_cleanup_errors: { icon: <FaExclamationTriangle />, label: 'Incomplete Signup Error', color: '#ef4444' },
      old_meetings_cleanup_errors: { icon: <FaExclamationTriangle />, label: 'Old Meetings Cleanup Error', color: '#ef4444' },
      
      // Fallback for unknown types
      unknown: { icon: <FaInfoCircle />, label: 'Unknown Operation', color: '#94a3b8' }
    };

    // # Collect all logs into a single array with type validation
    const allLogs = [];
    
    // # Add null check and type validation before processing
    Object.entries(analyticsData).forEach(([type, logs]) => {
      // # Skip if logs is null or not an object
      if (!logs || typeof logs !== 'object') return;

      Object.entries(logs).forEach(([logId, logData]) => {
        // # Skip if logData is null or not an object
        if (!logData || typeof logData !== 'object') return;

        // # Ensure timestamp exists, if not use a fallback
        if (!logData.timestamp) {
          logData.timestamp = new Date().toISOString();
        }

        // # Determine the correct type mapping with validation
        let logType = type;
        
        // # If the log contains a type field, use that instead
        if (logData.type && typeof logData.type === 'string') {
          // # Check if the type exists in logTypes, otherwise fallback to unknown
          logType = logTypes[logData.type] ? logData.type : 'unknown';
        } else if (!logTypes[type]) {
          // # If the collection type doesn't exist in logTypes, fallback to unknown
          logType = 'unknown';
        }

        // # Add log with validated data
        allLogs.push({
          id: logId,
          type: logType,
          ...logData,
          // # Ensure stats object exists
          stats: logData.stats || {},
          // # Ensure error object exists if present
          error: logData.error || null
        });
      });
    });

    // # Sort logs by timestamp with validation
    allLogs.sort((a, b) => {
      try {
        return new Date(b.timestamp) - new Date(a.timestamp);
      } catch (error) {
        return 0; // # Keep original order if dates are invalid
      }
    });

    // # Format timestamp
    const formatTimestamp = (timestamp) => {
      return new Date(timestamp).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    };

    // # Format execution time
    const formatExecutionTime = (ms) => {
      if (ms < 1000) return `${ms}ms`;
      return `${(ms / 1000).toFixed(2)}s`;
    };

    // # Render stats summary
    const renderStatsSummary = (stats) => {
      if (!stats) return null;
      
      const summaryItems = [];
      Object.entries(stats).forEach(([key, value]) => {
        const label = key.replace(/([A-Z])/g, ' $1')
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        summaryItems.push(`${label}: ${value}`);
      });
      
      return (
        <div className="tech_log_stats">
          {summaryItems.map((item, index) => (
            <span key={index} className="tech_log_stat_item">{item}</span>
          ))}
        </div>
      );
    };

    return (
      <div className="tech_logs_table_wrapper">
        <table className="tech_logs_table">
          <thead>
            <tr>
              <th className="tech_col_timestamp">
                <FaClock /> Timestamp
              </th>
              <th className="tech_col_type">
                <FaTag /> Type
              </th>
              <th className="tech_col_execution">
                <FaBolt /> Execution
              </th>
              <th className="tech_col_details">
                <FaInfoCircle /> Details
              </th>
            </tr>
          </thead>
          <tbody>
            {allLogs.map((log) => (
              <tr key={log.id} className={log.type.includes('error') ? 'tech_log_row_error' : 'tech_log_row'}>
                <td className="tech_col_timestamp">
                  {formatTimestamp(log.timestamp)}
                </td>
                <td className="tech_col_type">
                  <div className="tech_log_type" style={{ '--type-color': logTypes[log.type].color }}>
                    {logTypes[log.type].icon}
                    <span>{logTypes[log.type].label}</span>
                  </div>
                </td>
                <td className="tech_col_execution">
                  {log.executionTime && formatExecutionTime(log.executionTime)}
                </td>
                <td className="tech_col_details">
                  {log.stats && renderStatsSummary(log.stats)}
                  {log.error && (
                    <div className="tech_log_error">
                      <FaExclamationCircle />
                      <span>{log.error.message}</span>
                    </div>
                  )}
                  {log.cutoffTime && (
                    <div className="tech_log_cutoff_info">
                      <FaCalendarAlt />
                      <span>Cutoff: {formatTimestamp(log.cutoffTime)}</span>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="tech_admin_page">
      <SharedHeader />
      
      <main className="tech_admin_content">
        <div className="tech_admin_header">
          <button 
            className="tech_back_button"
            onClick={() => navigate('/admin/analytics')}
          >
            <i className="fas fa-arrow-left"></i> Back to Analytics
          </button>
          <h1>Technical Analytics</h1>
        </div>

        {/* Tab navigation */}
        <div className="tech_tabs">
          <button 
            className={`tech_tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            Technical Analytics
          </button>
          <button 
            className={`tech_tab ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
          >
            Technical Logs
          </button>
        </div>

        {months.length > 0 && (
          <div className="tech_controls">
            <select 
              value={selectedMonth} 
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="tech_month_select"
            >
              {months.map(month => (
                <option key={month} value={month}>
                  {month.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Debug info - can be removed in production */}
        {debug && (
          <div className="tech_debug">
            <strong>Debug:</strong> {debug}
          </div>
        )}

        {error ? (
          <div className="tech_no_data">{error}</div>
        ) : loading ? (
          <div className="tech_loading">Loading analytics data...</div>
        ) : (
          <>
            {/* Analytics Tab Content */}
            {activeTab === 'analytics' && renderAnalyticsSection()}

            {/* Logs Tab Content */}
            {activeTab === 'logs' && renderRawLogs()}
          </>
        )}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default TechnicalAnalytics; 